import React, { Fragment, useCallback, useEffect, useMemo } from "react";
import { useState } from "react";
import { useNavigate } from "react-router";

import {
  Destination,
  getDefaultAuthMethod,
  getDefaultBucketVendor,
  getDefaultStorageAuthMethod,
  PreparedDestination,
  prepareDestinationWithForm,
  StorageVendor,
  useDestination,
  useDestinationForm,
} from "@prequel/react";
import { Button, ButtonStyle, Checkbox, Spinner } from "@prequel/shared-components";
import { AlertCircleIcon, AlertTriangleIcon, CheckIcon, EllipsisIcon, SpinnerIcon } from "@prequel/shared-components";

import ConstWrapper from "./ConstWrapper";
import { getBucketVendor, isFixedField } from "./helpers";
import InputWrapper from "./InputWrapper";
import RadioWrapper from "./RadioWrapper";
import SelectWrapper from "./SelectWrapper";
import TextAreaWrapper from "./TextAreaWrapper";

import { useCreateDestination, useTestDestination } from "@/api/queries/destinations";
import { MagicLink } from "@/api/services/magic-links";
import BrandedLogo from "@/components/BrandedLogo";
import { env } from "@/env";

const classNames = (...classes: string[]) => {
  return classes.filter(Boolean).join(" ");
};

const ALL_CURRENT_FUTURE_MODELS = ["*"];

type ConnectProps = {
  orgID: string;
  linkID: string;
  magicLink: MagicLink;
  prodEnv: boolean;
};
const Connect = ({ magicLink, orgID, linkID, prodEnv }: ConnectProps) => {
  const testDestinationMutation = useTestDestination();
  const createDestinationMutation = useCreateDestination();

  const destinationTest = {
    status: testDestinationMutation.isPending
      ? ("processing" as const)
      : testDestinationMutation.isSuccess
        ? testDestinationMutation.data.status
        : testDestinationMutation.isError
          ? ("error" as const)
          : undefined,
    message: testDestinationMutation.isSuccess
      ? testDestinationMutation.data.message
      : testDestinationMutation.isError
        ? (testDestinationMutation.error as any)?.error?.message
        : undefined,
  };

  const destinationCreate = {
    status: createDestinationMutation.isPending
      ? ("processing" as const)
      : createDestinationMutation.isSuccess
        ? ("success" as const)
        : createDestinationMutation.isError
          ? ("error" as const)
          : undefined,
  };
  const bucketVendor = getBucketVendor(magicLink.bucket_vendor, magicLink.catalog);
  const storage = bucketVendor ? (bucketVendor as StorageVendor) : undefined;
  const [destination, setDestination] = useDestination({
    name: magicLink.destination_name,
    vendor: magicLink.vendor,
    host: magicLink.host,
    project_id: magicLink.host,
    bucket_name: magicLink.bucket_name,
    container_name: magicLink.bucket_name,
    storage_account_name: magicLink.bucket_access_id,
    enabled_models: ALL_CURRENT_FUTURE_MODELS,
    metastore: magicLink.metastore as any,
    catalog: magicLink.catalog,
    recipient_id: magicLink.recipient_id,
    storage: storage,
    bucket_vendor: bucketVendor,
  });
  const form = useDestinationForm(destination, orgID, {
    host: env.VITE_APP_API_SERVER,
    vendorQuery: magicLink.vendor,
  });

  const [modelsError, setModelsError] = useState<string>();
  const [allFutureModels, setAllFutureModels] = useState<boolean>(true);

  const preparedDestination: PreparedDestination = useMemo(
    () => prepareDestinationWithForm(destination, form),
    [destination, form],
  );

  const selectedVendor = useMemo(() => {
    if (form) {
      const { fields } = form[0];
      if (fields[0].form_element === "select") {
        return fields[0].enum?.find(({ key }) => key === magicLink.vendor);
      }
    }
  }, [form, magicLink.vendor]);

  const navigate = useNavigate();

  useEffect(() => {
    const defaultAuthMethod = getDefaultAuthMethod(magicLink.vendor);
    const defaultBucketVendor = getDefaultBucketVendor(magicLink.vendor);
    let defaultStorageAuthMethod;
    if (destination.storage) {
      defaultStorageAuthMethod = getDefaultStorageAuthMethod(destination.storage);
    }
    setDestination((currentDestination) => ({
      ...currentDestination,
      auth_method: defaultAuthMethod,
      bucket_auth_method: defaultStorageAuthMethod,
      bucket_vendor: currentDestination.bucket_vendor || defaultBucketVendor,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDestinationField = useCallback(
    (
      key: keyof Destination | "primary_public_key" | "secondary_public_key",
      value: string | string[] | boolean | number | undefined,
    ) =>
      setDestination((currentDestination) => ({
        ...currentDestination,
        [key]: value,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // setDestination from SDK hook is stable
  );

  // Reset test on form change
  useEffect(() => {
    testDestinationMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [destination]);

  useEffect(() => {
    setModelsError(undefined);
  }, [destination.enabled_models]);

  useEffect(() => {
    if (allFutureModels) {
      setDestinationField("enabled_models", ALL_CURRENT_FUTURE_MODELS);
    } else {
      setDestinationField("enabled_models", magicLink.available_models);
    }
  }, [allFutureModels, magicLink.available_models, setDestinationField]);

  const updateEnabledModels = (isEnabled: boolean, modelName: string) => {
    const updatedModels = isEnabled
      ? [...(destination.enabled_models ?? []), modelName]
      : destination.enabled_models?.filter((m) => m !== modelName);
    setDestinationField("enabled_models", updatedModels);
  };

  // Handle destination submission
  const onConnectHandler = () => {
    let redirect = `/orgs/${orgID}/links/${linkID}/success`;
    if (!prodEnv) {
      redirect = "/staging" + redirect;
    }
    createDestinationMutation.mutate(
      {
        magiclinkargs: { orgID: orgID, linkID: linkID, isProd: prodEnv },
        destination: preparedDestination,
      },
      {
        onSuccess: () => {
          navigate(redirect, {
            replace: false,
            state: { redirectUrl: magicLink.redirect_url },
          });
        },
      },
    );
  };

  // Handle destination test
  const onTestHandler = () => {
    if (!destination.enabled_models || destination.enabled_models?.length === 0) {
      setModelsError("This is a required field");
      return;
    }

    testDestinationMutation.mutate({
      magiclinkargs: { orgID, linkID, isProd: prodEnv },
      destination: preparedDestination,
    });
  };

  // Intercept native form submission, prevent default, and run test
  // We use the default form submission event so that we can borrow the browsers built-in support for handling missing required fields
  const onSubmitHandler = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onTestHandler();
  };

  return (
    <>
      <nav className="bg-white-100 border-b border-gray-200 bg-white" aria-label="Global">
        <div
          className="
            mx-auto max-w-7xl px-2
            sm:px-4
            lg:px-8
          "
        >
          <div className="flex h-16 justify-between">
            <div
              className="
                flex items-center px-2
                lg:px-0
              "
            >
              <div className="inline-flex w-36 shrink-0 items-center justify-center rounded-lg">
                <BrandedLogo orgID={orgID} />
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main
        className="
          mx-auto max-w-lg px-4 pt-10 pb-12
          lg:pb-16
        "
      >
        {form ? (
          <>
            <form onSubmit={onSubmitHandler}>
              <div className="space-y-6">
                {form.map((section) => {
                  return (
                    <Fragment key={section.id}>
                      <div>
                        {section.id === 1 ? (
                          <div>
                            <h1 className="text-lg/6 font-medium text-gray-900">Connect destination</h1>
                          </div>
                        ) : (
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            {section.title}
                          </label>
                        )}
                        {section.subtitle ? (
                          <div className="mt-1 mb-4">
                            <p className="mt-1 text-sm text-gray-500">{section.subtitle}</p>
                          </div>
                        ) : (
                          <br />
                        )}
                        <div className="space-y-4">
                          {section.fields.map((field) => {
                            if (field.const) {
                              return (
                                <div key={field.name}>
                                  <ConstWrapper field={field} setDestinationField={setDestinationField} />
                                </div>
                              );
                            } else if (field.form_element === "select") {
                              return (
                                <div key={field.name}>
                                  <SelectWrapper
                                    field={field}
                                    destination={destination}
                                    setDestinationField={setDestinationField}
                                    disabled={
                                      destinationTest?.status === "processing" ||
                                      destinationCreate.status === "processing" ||
                                      isFixedField(field.name, magicLink.catalog)
                                    }
                                  />
                                </div>
                              );
                            } else if (field.form_element === "input") {
                              return (
                                <div key={field.name}>
                                  <InputWrapper
                                    field={field}
                                    destination={destination}
                                    setDestinationField={setDestinationField}
                                    disabled={
                                      destinationTest?.status === "processing" ||
                                      destinationCreate.status === "processing" ||
                                      isFixedField(field.name, magicLink.catalog)
                                    }
                                  />
                                </div>
                              );
                            } else if (field.form_element === "textarea") {
                              return (
                                <div key={field.name}>
                                  <TextAreaWrapper
                                    field={field}
                                    destination={destination}
                                    setDestinationField={setDestinationField}
                                    disabled={
                                      destinationTest?.status === "processing" ||
                                      destinationCreate.status === "processing" ||
                                      isFixedField(field.name, magicLink.catalog)
                                    }
                                  />
                                </div>
                              );
                            } else if (field.form_element === "radio") {
                              return (
                                <div key={field.name}>
                                  <RadioWrapper
                                    field={field}
                                    destination={destination}
                                    setDestinationField={setDestinationField}
                                    disabled={
                                      destinationTest?.status === "processing" ||
                                      destinationCreate.status === "processing" ||
                                      isFixedField(field.name, magicLink.catalog)
                                    }
                                  />
                                </div>
                              );
                            }
                          })}
                        </div>
                      </div>
                      <div className="h-px w-full bg-gray-200" /> {/* Divider  */}
                      {section.id === 1 && selectedVendor && (
                        <div>
                          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                            {`Configure your ${selectedVendor.display} connection`}
                          </label>
                          <div className="mt-1">
                            <p className="mt-1 text-sm text-gray-500">
                              If you’re unsure how to get started or want to read about our recommended configuration,{" "}
                              <a
                                href={magicLink.destination_docs_override || selectedVendor.docs}
                                target="_blank"
                                rel="noreferrer"
                                className="
                                  font-medium text-primary-600
                                  hover:text-primary-500
                                "
                              >
                                {`view our documentation on ${selectedVendor.display}.`}
                              </a>
                            </p>
                          </div>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
                {/* Special case for default value of "*", don't show model selectors */}
                {(magicLink.available_models.length > 1 ||
                  (magicLink.available_models.length == 1 && magicLink.available_models[0] !== "*")) && (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700">Select what models to receive</label>
                    <div className="space-y-2">
                      {magicLink.available_models.map((m) => (
                        <Checkbox
                          key={m}
                          id={m}
                          label={m}
                          checked={destination.enabled_models?.includes(m) || allFutureModels}
                          setChecked={(isChecked: boolean) => updateEnabledModels(isChecked, m)}
                          disabled={
                            allFutureModels ||
                            destinationTest?.status === "processing" ||
                            destinationCreate.status === "processing"
                          }
                        />
                      ))}
                      <Checkbox
                        key={"all_models_present_future"}
                        id={"all_models_present_future"}
                        label={"All current and future models"}
                        description={"Any new tables added in the future will be synced automatically"}
                        checked={allFutureModels}
                        disabled={destinationTest?.status === "processing" || destinationCreate.status === "processing"}
                        setChecked={(isChecked: boolean) => setAllFutureModels(isChecked)}
                      />
                    </div>
                    {modelsError && <p className="mt-1 text-xs font-medium text-red-600">{modelsError}</p>}
                  </div>
                )}
                <div className="relative pt-4">
                  <div
                    className="
                      overflow-hidden rounded-lg border border-gray-300 shadow-sm
                      focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500
                    "
                  >
                    <div className="p-4">
                      <div className="flex">
                        <div className="shrink-0">
                          <div
                            className={classNames(
                              "mx-auto flex h-10 w-10 items-center justify-center rounded-full",
                              destinationTest.status === "success" ? "bg-emerald-100" : "",
                              destinationTest.status === "error" ? "bg-red-100" : "",
                              destinationTest.status === "processing" ? "bg-gray-100" : "",
                              !destinationTest.status ? "bg-gray-100" : "",
                            )}
                          >
                            {destinationTest.status === "success" && (
                              <CheckIcon className="size-5 text-emerald-600" aria-hidden="true" />
                            )}
                            {destinationTest.status === "error" && (
                              <AlertCircleIcon className="size-5 text-red-600" aria-hidden="true" />
                            )}
                            {destinationTest.status === "processing" && (
                              <EllipsisIcon className="size-5 text-gray-600" aria-hidden="true" />
                            )}
                            {!destinationTest.status && (
                              <AlertTriangleIcon className="size-5 text-gray-600" aria-hidden="true" />
                            )}
                          </div>
                        </div>
                        <div className="mt-1 ml-3">
                          <h3 className="text-lg font-medium text-gray-800">
                            {destinationTest.status === "success" && "Connection successful!"}
                            {destinationTest.status === "error" && "Error connecting to destination."}
                            {destinationTest.status === "processing" && "Testing connection..."}
                            {!destinationTest.status && "Test connection before saving."}
                          </h3>
                          <div className="my-2 text-sm text-gray-700">
                            <p>
                              {destinationTest.status === "success" &&
                                "Connection was successfully established to this destination. The destination details can now be saved."}
                              {destinationTest.status === "error" && (
                                <>
                                  <span className="mb-4 block">
                                    Try updating the destination details and try again.
                                  </span>
                                  <code className="text-xs" style={{ wordBreak: "break-word" }}>
                                    {destinationTest.message}
                                  </code>
                                </>
                              )}
                              {destinationTest.status === "processing" && ""}
                              {!destinationTest.status && ""}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Spacer element to match the height of the toolbar */}
                    <div aria-hidden="true">
                      <div className="h-px" />
                      <div className="py-2">
                        <div className="py-px">
                          <div className="h-9" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="absolute inset-x-px bottom-0">
                    <div
                      className="
                        flex items-center justify-between space-x-3 border-t border-gray-200 p-2
                        sm:px-3
                      "
                    >
                      <div className="flex">
                        <div className="shrink-0">
                          <button
                            type="submit"
                            disabled={
                              destinationTest.status === "processing" || destinationCreate.status === "processing"
                            }
                            name="test"
                            id="test"
                            className={classNames(
                              "inline-flex rounded-md px-4 py-2 text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:cursor-not-allowed",
                              destinationTest.status === "success"
                                ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                : "",
                              destinationTest.status === "error"
                                ? "bg-emerald-500 text-white hover:bg-emerald-600"
                                : "",
                              destinationTest.status === "processing"
                                ? "border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                : "",
                              !destinationTest.status ? "bg-emerald-500 text-white hover:bg-emerald-600" : "",
                            )}
                          >
                            {destinationTest.status === "processing" ? (
                              <>
                                <SpinnerIcon className="mr-3 size-5 animate-spin text-gray-500" viewBox="0 0 24 24" />{" "}
                                Testing Connection...
                              </>
                            ) : (
                              "Test Connection"
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
            <div className="flex justify-end pt-6">
              <Button
                onClick={onConnectHandler}
                type={
                  destinationTest.status !== "success"
                    ? ButtonStyle.SECONDARY // If the test is not successful, this is not the primary button to highlight.
                    : ButtonStyle.PRIMARY // If the test is successful, this is the primary action -- submitting the form
                }
                disabled={
                  destinationTest.status !== "success" || // If the test is not successful, submission is prevented
                  destinationCreate.status === "processing" // If the form has been submitted and is waiting for a response, disable a second click
                }
                text={
                  <div className="flex">
                    {destinationCreate.status === "processing" ? (
                      <div className="flex">
                        <Spinner.Inline className="mr-3 text-white" />
                        Saving Destination...
                      </div>
                    ) : (
                      "Save Destination"
                    )}
                  </div>
                }
              />
            </div>
          </>
        ) : (
          <Spinner />
        )}
      </main>
    </>
  );
};

export default Connect;
